import { Alert, Button, Form, Modal, Select, Tabs } from "antd";
import type { Project } from "@zhihaoscm/entity";
import { PERMISSION, PROJECT_STATE, PURCHASE_TYPE, ROLE_CODE } from "@zhihaoscm/meta";
import { formatNumber } from "@zhihaoscm/utils";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { projectService } from "@/api/projectService";
import { purchaseInvoiceService } from "@/api/purchaseInvoiceService";
import { purchaseReconciliationService } from "@/api/purchaseReconciliationService";
import { Breadcrumb, DetailHeader, InfoCard, ProTable, ZhBaseItem, ZhGrid, type ProColumn } from "@/components/common";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";
import type {
    PurchaseInvoice,
    PurchaseInvoiceForm,
    PurchaseInvoiceVo
} from "../../../../../common/types/purchaseInvoice";
import type { PurchaseReconciliation } from "../../../../../common/types/purchaseReconciliation";
import { RECONCILIATION_TYPES } from "../../../../../common/enum/reconciliation";
import "./console.css";

/** 开票列表通过 buyerInvoice 区分开票申请和下游开票。 */
export function PurchaseInvoiceListPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const userInfo = useUserSession(state => state.userInfo);
    const accountType = useAccountSession(state => state.accountType);
    const proxyId = useAccountSession(state => state.proxyActualAccount.id);
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const [buyerInvoice, setBuyerInvoice] = useState(() => searchParams.get("type") === "true");
    const [refreshKey, setRefreshKey] = useState(0);
    const permissions = (userInfo as any).permissions as number[] | undefined;
    const hasInvoicePermission =
        accountType !== AccountType.SUB_ACCOUNT || Boolean(permissions?.includes(PERMISSION.INVOICE.code));
    const showApplyColumns = isSupplier || (isSupplyChain && !buyerInvoice);
    /** 删除按钮与旧 getOperationButtons 保持同一账号及子账号权限门禁。 */
    const remove = (row: PurchaseInvoice) =>
        Modal.confirm({
            title: "是否删除",
            onOk: async () => {
                await purchaseInvoiceService.delete(row.id);
                setRefreshKey(value => value + 1);
            }
        });
    const columns = useMemo<ProColumn<PurchaseInvoice>[]>(
        () => [
            {
                key: "id",
                title: "编号",
                minWidth: 114,
                search: { key: "projectKeyword", type: "input", placeholder: "请输入编号或项目名称", span: 6, order: 1 },
                render: (_, row) => (
                    <Button
                        className="invoice-code-link"
                        type="link"
                        onClick={() => navigate(`/console/invoice/detail?id=${row.id}&type=${buyerInvoice}`)}
                    >
                        {row.id}
                    </Button>
                )
            },
            { key: "projectName", title: "项目", minWidth: 142 },
            {
                key: showApplyColumns ? "applyEnterprise" : "invoiceEnterprise",
                title: showApplyColumns ? "申请单位" : "开票单位",
                minWidth: 142,
                search: {
                    key: showApplyColumns ? "applyUnitName" : "invoiceUnitName",
                    type: "input",
                    placeholder: showApplyColumns ? "请输入申请单位名称" : "请输入开票单位名称",
                    order: 2
                },
                render: (_, row) => {
                    const enterprise = showApplyColumns ? row.applyEnterprise : row.invoiceEnterprise;
                    return enterprise?.name || enterprise?.legalRepresentative || enterprise?.realName || "--";
                }
            },
            {
                key: "amount",
                title: "金额（￥）",
                minWidth: 142,
                align: "right",
                headerAlign: "right",
                render: (_, row) => <span className="invoice-amount">{formatNumber(row.amount, 2)}</span>
            },
            ...((isSupplyChain && showApplyColumns) || (isCustomer && !showApplyColumns)
                ? [
                      {
                          key: "operation",
                          title: "操作",
                          width: 160,
                          fixed: "right" as const,
                          render: (_: unknown, row: PurchaseInvoice) =>
                              String(proxyId) === String(row.applyCustomerId) && hasInvoicePermission ? (
                                  <Button
                                      className="invoice-delete-link"
                                      danger
                                      type="link"
                                      onClick={() => remove(row)}
                                  >
                                      删除
                                  </Button>
                              ) : (
                                  "--"
                              )
                      }
                  ]
                : [])
        ],
        [buyerInvoice, hasInvoicePermission, isCustomer, isSupplyChain, navigate, proxyId, showApplyColumns]
    );
    /** 服务端该分页沿用旧实体响应，覆盖 BaseService 的 VO 泛型声明。 */
    const request = async (params: Record<string, unknown>) => {
        const page = await purchaseInvoiceService.paging({ ...params, buyerInvoice } as never);
        return page as unknown as { list: PurchaseInvoice[]; total: number };
    };
    const canCreate = isCustomer || (isSupplyChain && !buyerInvoice && hasInvoicePermission);
    return (
        <section className="purchase-invoice-list-page section-card">
            <Breadcrumb routes={[{ name: "采购" }, { name: "开票列表" }]} />
            {isSupplyChain ? (
                <Tabs
                    activeKey={String(buyerInvoice)}
                    onChange={key => setBuyerInvoice(key === "true")}
                    items={[
                        { key: "false", label: "开票申请" },
                        { key: "true", label: "下游开票" }
                    ]}
                />
            ) : null}
            <div className="purchase-invoice-table">
                <ProTable
                    key={`${buyerInvoice}-${refreshKey}`}
                    title={isSupplyChain ? undefined : "开票列表"}
                    rowKey="id"
                    columns={columns}
                    request={request}
                    header={
                        canCreate
                            ? () => (
                                  <Button type="primary" onClick={() => navigate("/console/invoice/form")}>
                                      新增
                                  </Button>
                              )
                            : undefined
                    }
                />
            </div>
        </section>
    );
}

/** 对账卡逐项对应旧 InvoiceReconciliationCard 与 ReceiptCard。 */
function InvoiceReconciliationCard({
    item,
    selected,
    selectable,
    onSelect,
    onClose,
    onTitle
}: {
    item: PurchaseReconciliation;
    selected?: boolean;
    selectable?: boolean;
    onSelect?: () => void;
    onClose?: () => void;
    onTitle?: () => void;
}) {
    const quantity = Number(item.quantity ?? 0);
    return (
        <article
            className={`invoice-reconciliation-card${selected ? " is-selected" : ""}`}
            onClick={selectable ? onSelect : undefined}
        >
            <header>
                {onTitle ? (
                    <button
                        onClick={event => {
                            event.stopPropagation();
                            onTitle();
                        }}
                        type="button"
                    >
                        {item.id}
                    </button>
                ) : (
                    <span>{item.id}</span>
                )}
                {selectable ? (
                    <i className={`iconfont ${selected ? "icon-Vector" : "icon-circle"}`} />
                ) : onClose ? (
                    <button aria-label={`移除${item.id}`} onClick={onClose} type="button">
                        <i className="iconfont icon-guanbi" />
                    </button>
                ) : null}
            </header>
            <dl>
                <div>
                    <dt>吨数</dt>
                    <dd>{formatNumber(quantity)}</dd>
                </div>
                <div>
                    <dt>单价</dt>
                    <dd>{quantity ? `¥${formatNumber(Number(item.actualAmount) / quantity, 2)}/吨` : "--"}</dd>
                </div>
                <div>
                    <dt>折扣金额</dt>
                    <dd>¥{formatNumber(item.actualAmount, 2)}</dd>
                </div>
                <div>
                    <dt>确认日期</dt>
                    <dd>{item.createdTime ? dayjs(item.createdTime).format("YYYY-MM-DD") : "--"}</dd>
                </div>
                <div>
                    <dt>对账日期</dt>
                    <dd>{item.reconciliationDate ? dayjs(item.reconciliationDate).format("YYYY-MM-DD") : "--"}</dd>
                </div>
            </dl>
        </article>
    );
}

/** 新增开票申请逐组件对应旧表单、项目摘要与关联对账弹窗。 */
export function PurchaseInvoiceFormPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [form] = Form.useForm<PurchaseInvoiceForm>();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const userInfo = useUserSession(state => state.userInfo);
    const proxyId = useAccountSession(state => state.proxyActualAccount.id);
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project>();
    const [options, setOptions] = useState<PurchaseReconciliation[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [confirmed, setConfirmed] = useState<PurchaseReconciliation[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const loadProjects = async (name?: string) =>
        setProjects(await projectService.noPaging({ states: String(PROJECT_STATE.PROCESSING.code), name } as never));
    useEffect(() => {
        void loadProjects();
    }, []);
    useEffect(() => {
        const projectId = Number(params.get("projectId"));
        if (!projectId) return;
        void projectService.getInfo(projectId).then(data => {
            setSelectedProject(data.project);
            setProjects(items => (items.some(item => item.id === data.project.id) ? items : [data.project, ...items]));
            form.setFieldValue("projectId", projectId);
        });
    }, [form, params]);
    /** 切换项目时清空已选对账单，避免跨项目提交。 */
    const changeProject = (projectId: number) => {
        setSelectedProject(projects.find(item => item.id === projectId));
        setSelectedIds([]);
        setConfirmed([]);
        form.setFieldValue("purchaseReconciliationIds", []);
    };
    const openReconciliations = async () => {
        const projectId = form.getFieldValue("projectId");
        if (!projectId || !selectedProject) {
            await form.validateFields(["projectId"]);
            return;
        }
        const projectVo = await projectService.getInfo(projectId);
        const type = PURCHASE_TYPE.DIRECT.match(projectVo.project.purType)
            ? RECONCILIATION_TYPES.ONE.code
            : isCustomer
              ? RECONCILIATION_TYPES.THREE.code
              : RECONCILIATION_TYPES.TWO.code;
        const query: any = { projectId, filterInvoice: true, type };
        if (isCustomer && type === RECONCILIATION_TYPES.THREE.code && proxyId) query.buyerId = proxyId;
        setOptions(await purchaseReconciliationService.selector(query));
        setDialogOpen(true);
    };
    const submit = async (values: PurchaseInvoiceForm) => {
        if (!selectedIds.length) {
            form.setFields([{ name: "purchaseReconciliationIds", errors: ["请选择对账单"] }]);
            return;
        }
        Modal.confirm({
            title: "提示",
            content: "是否提交",
            onOk: async () => {
                setSubmitting(true);
                try {
                    const data = (await purchaseInvoiceService.create({
                        ...values,
                        purchaseReconciliationIds: selectedIds
                    })) as any;
                    if ((!data || !data.errors) && data?.status !== 400) navigate(-1);
                } finally {
                    setSubmitting(false);
                }
            }
        });
    };
    const customerName =
        (userInfo.customer as any)?.institutionName ||
        (userInfo.customer as any)?.name ||
        (userInfo.customer as any)?.realName ||
        "--";
    const applyUnit = selectedProject
        ? isCustomer
            ? customerName
            : selectedProject.supplyChainEnterprise?.name || "--"
        : "--";
    const invoiceUnit = selectedProject
        ? PURCHASE_TYPE.DIRECT.match(selectedProject.purType)
            ? selectedProject.supplierEnterprise?.name || "--"
            : isCustomer
              ? selectedProject.supplyChainEnterprise?.name || "--"
              : selectedProject.supplierEnterprise?.name || "--"
        : "--";
    return (
        <section className="purchase-invoice-form-page">
            <div className="section-card">
                <Breadcrumb
                    routes={[
                        { name: "采购" },
                        { name: "开票列表", path: "/console/invoice" },
                        { name: "开票详情" }
                    ]}
                />
                <h1>新增开票申请</h1>
                <Form form={form} layout="vertical" onFinish={submit}>
                    <div className="invoice-form-project">
                        <Form.Item
                            name="projectId"
                            label="关联项目"
                            required
                            rules={[{ required: true, message: "请选择项目" }]}
                        >
                            <Select
                                showSearch
                                filterOption={false}
                                placeholder="请下拉选择或搜索项目"
                                options={projects.map(item => ({ label: item.name, value: item.id }))}
                                onSearch={loadProjects}
                                onChange={changeProject}
                            />
                        </Form.Item>
                        {selectedProject ? (
                            <div className="invoice-project-summary">
                                <ZhGrid cols={2}>
                                    <ZhBaseItem label="申请单位" value={applyUnit} />
                                    <ZhBaseItem label="开票单位" value={invoiceUnit} />
                                    <ZhBaseItem label="品类" span={2} value={selectedProject.productTypeName || "--"} />
                                </ZhGrid>
                            </div>
                        ) : null}
                    </div>
                    <Form.Item
                        name="purchaseReconciliationIds"
                        required
                        label={
                            <span>
                                关联对账单
                                <Button
                                    className="invoice-associate-link"
                                    type="link"
                                    onClick={() => void openReconciliations()}
                                >
                                    关联
                                    <i className="iconfont icon-Frame-3" />
                                </Button>
                            </span>
                        }
                        rules={[{ required: true, message: "请选择对账单" }]}
                    >
                        <input type="hidden" value={selectedIds.join(",")} readOnly />
                    </Form.Item>
                    {confirmed.length ? (
                        <div className="invoice-selected-grid">
                            {confirmed.map((item, index) => (
                                <InvoiceReconciliationCard
                                    item={item}
                                    key={item.id}
                                    onTitle={() => navigate(`/console/reconciliation/detail?id=${item.id}`)}
                                    onClose={() => {
                                        const next = selectedIds.filter(id => id !== item.id);
                                        setSelectedIds(next);
                                        setConfirmed(list => list.filter((_, itemIndex) => itemIndex !== index));
                                        form.setFieldValue("purchaseReconciliationIds", next);
                                    }}
                                />
                            ))}
                        </div>
                    ) : null}
                </Form>
                <Modal
                    open={dialogOpen}
                    title="关联对账单"
                    width={584}
                    onCancel={() => setDialogOpen(false)}
                    footer={
                        <>
                            <Button type="primary" onClick={() => setDialogOpen(false)}>
                                返回
                            </Button>
                            {options.length ? (
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        setConfirmed(options.filter(item => selectedIds.includes(item.id)));
                                        form.setFieldValue("purchaseReconciliationIds", selectedIds);
                                        setDialogOpen(false);
                                    }}
                                >
                                    确定
                                </Button>
                            ) : null}
                        </>
                    }
                >
                    <div className="invoice-reconciliation-options">
                        {options.length ? (
                            options.map(item => (
                                <InvoiceReconciliationCard
                                    item={item}
                                    key={item.id}
                                    selectable
                                    selected={selectedIds.includes(item.id)}
                                    onSelect={() =>
                                        setSelectedIds(ids =>
                                            ids.includes(item.id) ? ids.filter(id => id !== item.id) : [...ids, item.id]
                                        )
                                    }
                                />
                            ))
                        ) : (
                            <div className="invoice-reconciliation-empty">暂无数据</div>
                        )}
                    </div>
                </Modal>
            </div>
            <div className="purchase-invoice-form-actions">
                <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                    申请开票
                </Button>
                <Button type="primary" onClick={() => navigate("/console/invoice")}>
                    返回
                </Button>
            </div>
        </section>
    );
}

/** 开票详情逐项对应旧详情头、信息卡、四列对账卡及权限操作。 */
export function PurchaseInvoiceDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const [info, setInfo] = useState<PurchaseInvoiceVo>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const load = async () => {
        if (!id) {
            setError(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            setInfo(await purchaseInvoiceService.getInfo(id));
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    // 旧 Vue 详情首屏静默等待数据，不额外展示页面级加载框。
    if (loading) return null;
    if (error || !info)
        return (
            <Alert
                type="error"
                showIcon
                message={id ? "开票详情加载失败" : "缺少开票 ID"}
                action={id ? <Button onClick={() => void load()}>重试</Button> : undefined}
            />
        );
    const invoice = info.purchaseInvoice;
    const buyerInvoice = params.get("type") === "true";
    const userInfo = useUserSession.getState().userInfo;
    const account = useAccountSession.getState();
    const permissions = (userInfo as any).permissions as number[] | undefined;
    const hasPermission =
        account.accountType !== AccountType.SUB_ACCOUNT || Boolean(permissions?.includes(PERMISSION.INVOICE.code));
    const canDelete = String(account.proxyActualAccount.id) === String(invoice.applyCustomerId) && hasPermission;
    const enterpriseName = (enterprise: any) =>
        enterprise?.name || enterprise?.legalRepresentative || enterprise?.realName || "--";
    const remove = () =>
        Modal.confirm({
            title: "是否删除",
            onOk: async () => {
                await purchaseInvoiceService.delete(invoice.id);
                navigate("/console/invoice");
            }
        });
    return (
        <section className="purchase-invoice-detail-page detail-container">
            <DetailHeader
                title={buyerInvoice ? "开票申请" : "下游开票详情"}
                leftInfos={[{ label: "编号", value: invoice.id }]}
                rightInfos={[
                    {
                        label: "更新时间",
                        value: invoice.updatedTime ? dayjs(invoice.updatedTime).format("YYYY-MM-DD HH:mm:ss") : "--"
                    }
                ]}
                menu={
                    <Breadcrumb
                        routes={[
                            { name: "采购" },
                            { name: "开票列表", path: "/console/invoice" },
                            { name: "新增开票" }
                        ]}
                    />
                }
            />
            <InfoCard title="开票信息">
                <ZhGrid cols={4}>
                    <ZhBaseItem label="项目" span={2}>
                        <button
                            className="invoice-detail-link"
                            onClick={() => navigate(`/console/project/detail?id=${info.project.id}`)}
                            type="button"
                        >
                            {info.project.name || "--"}
                        </button>
                    </ZhBaseItem>
                    <ZhBaseItem label="品类" span={2} value={info.project.productTypeName || "--"} />
                    <ZhBaseItem label="申请单位" value={enterpriseName(invoice.applyEnterprise)} />
                    <ZhBaseItem label="开票单位" value={invoice.invoiceEnterprise?.name || "--"} />
                    <ZhBaseItem label="金额" value={`¥${formatNumber(invoice.amount, 2)}`} />
                </ZhGrid>
            </InfoCard>
            {info.purchaseReconciliations ? (
                <InfoCard title="对账单">
                    <div className="invoice-detail-reconciliations">
                        {info.purchaseReconciliations.map(item => {
                            const quantity = Number(item.quantity || 0);
                            return (
                                <article className="invoice-detail-reconciliation" key={item.id}>
                                    <header>
                                        <button
                                            onClick={() => navigate(`/console/reconciliation/detail?id=${item.id}`)}
                                            type="button"
                                        >
                                            {item.id}
                                        </button>
                                    </header>
                                    <div>
                                        <p>
                                            <span>吨数</span>
                                            <strong>{formatNumber(item.quantity)}</strong>
                                        </p>
                                        <p>
                                            <span>单价</span>
                                            <strong>
                                                {quantity
                                                    ? `¥${formatNumber(Number(item.actualAmount) / quantity, 2)}/吨`
                                                    : "--"}
                                            </strong>
                                        </p>
                                        <p>
                                            <span>折扣金额</span>
                                            <strong>¥{formatNumber(item.actualAmount, 2)}</strong>
                                        </p>
                                        <p>
                                            <span>对账日期</span>
                                            <strong>
                                                {item.reconciliationDate
                                                    ? dayjs(item.reconciliationDate).format("YYYY-MM-DD")
                                                    : "--"}
                                            </strong>
                                        </p>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </InfoCard>
            ) : null}
            <div className="purchase-invoice-detail-actions">
                {canDelete ? (
                    <Button danger type="primary" onClick={remove}>
                        删除
                    </Button>
                ) : null}
                <Button type="primary" onClick={() => navigate("/console/invoice")}>
                    返回
                </Button>
            </div>
        </section>
    );
}
